import React, { createContext, useContext, useState, useEffect } from 'react';
import MarginCalculatorContext from './MarginCalculatorContext';
import { useLoading } from '@/utils/LoadingUtil';
import {
  getAllDistinctProductName,
  getAllMinorGroupList,
  uploadProductFile,
  downloadProductFileCSV,
  downloadProductFileExcel,
  calculateMargin,
  calculateMarginByFile,
} from '@/services/MarginCalculatorService';
import { showAlert } from '@/components';

/**
 * Provides context for margin calculator operations.
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components.
 * @returns {JSX.Element} The context provider component.
 */
const MarginCalculatorContextProvider = ({ children }) => {
  const {
    isLoading,
    startLoading,
    stopLoading,
    setloadingText,
    loadingText,
    downloadPercentage,
    setDownloadPercentage,
  } = useLoading();
  const [marginData, setMarginData] = useState([]);

  /**
   * Uploads a margin calculation file.
   * @param {File} file - The file to upload.
   */
  const uploadMarginCalculationFile = async (file) => {
    startLoading();
    setloadingText('Uploading file...');
    try {
      await uploadProductFile(file, {
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 0;
          const current = progressEvent.loaded || 0;
          const percentage = Math.floor((current / total) * 100);
          setDownloadPercentage(percentage);
          console.log(`Uploading file: ${percentage}%`);
          setloadingText(`Uploading file: ${percentage}%`);
        },
      });
      setloadingText('Processing data...');
    } catch (error) {
      // showAlert("error", error.message || "An error occurred");
    } finally {
      stopLoading();
      setDownloadPercentage(null);
      setloadingText('');
    }
  };

  /**
   * Fetches the list of main products.
   * @returns {Promise<Array>} The list of main products.
   */
  const getMainProducts = async () => {
    try {
      startLoading();
      const res = await getAllDistinctProductName();
      return res;
    } catch (error) {
      showAlert(error);
    } finally {
      stopLoading();
    }
  };

  /**
   * Fetches the list of child products for a given main product.
   * @param {string} mainProduct - The main product name.
   * @returns {Promise<Array>} The list of child products.
   */
  const getChildProducts = async (mainProduct) => {
    try {
      startLoading();
      const res = await getAllMinorGroupList(mainProduct);
      return res;
    } catch (error) {
      showAlert(error);
    } finally {
      stopLoading();
    }
  };

  const downloadFile = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
  };

  /**
   * Downloads the product file as a CSV.
   */
  const downloadCSV = async () => {
    startLoading();
    setloadingText('Downloading CSV...');
    try {
      const data = await downloadProductFileCSV({
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || 0;
          const current = progressEvent.loaded || 0;
          const percentage = Math.floor((current / total) * 100);
          setDownloadPercentage(percentage);
          console.log(`Downloading CSV: ${percentage}%`);
          setloadingText(`Downloading CSV: ${percentage}%`);
        },
      });
      downloadFile(data, 'product_file.csv');
      showAlert('success', 'CSV downloaded successfully');
    } catch (error) {
      // showAlert("error", error.message || "An error occurred");
    } finally {
      stopLoading();
      setDownloadPercentage(null);
      setloadingText('');
    }
  };

  /**
   * Downloads the product file as an Excel file.
   */
  const downloadExcel = async () => {
    startLoading();
    setloadingText('Downloading Excel...');
    try {
      const data = await downloadProductFileExcel({
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || 0;
          const current = progressEvent.loaded || 0;
          const percentage = Math.floor((current / total) * 100);
          setDownloadPercentage(percentage);
          console.log(`Downloading Excel: ${percentage}%`);
          setloadingText(`Downloading Excel: ${percentage}%`);
        },
      });
      downloadFile(data, 'product_file.xlsx');
      showAlert('success', 'Excel downloaded successfully');
    } catch (error) {
      // showAlert("error", error.message || "An error occurred");
    } finally {
      stopLoading();
      setDownloadPercentage(null);
      setloadingText('');
    }
  };

  /**
   * Calculates the margin for given products and cost.
   * @param {string} mainProd - The main product name.
   * @param {string} childProd - The child product name.
   * @param {string} cost1 - The cost as a string.
   * @param {number} conversionFactor - The conversion factor.
   */
  const calculateMarginApi = async (
    mainProd,
    childProd,
    cost1,
    conversionFactor,
  ) => {
    // Convert cost to float
    const initialCost = cost1 === '' ? '' : parseFloat(cost1);
    // Round the cost based on the specified rules
    const roundedCost = Math.round(initialCost);
    const finalCost = initialCost > 0 && initialCost < 1 ? 1 : roundedCost;
    // Set the final cost
    const cost = finalCost;

    console.log('cost', cost, 'cost1', cost1);
    try {
      startLoading();
      const res = await calculateMargin(
        mainProd,
        childProd,
        cost,
        cost1,
        conversionFactor,
      );
      setMarginData([res]);
    } catch (error) {
    } finally {
      stopLoading();
    }
  };

  const calculateMarginByProductFile = async (file) => {
    setloadingText('Uploading file...');
    try {
      startLoading();
      const uploadResponse = await calculateMarginByFile(file, {
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 0;
          const current = progressEvent.loaded || 0;
          const percentage = Math.floor((current / total) * 100);
          setDownloadPercentage(percentage);
          console.log(`Uploading file: ${percentage}%`);
          setloadingText(`Uploading file: ${percentage}%`);
        },
      });

      const downloadResponse = await calculateMarginByFile(file, {
        onDownloadProgress: (progressEvent) => {
          const total = progressEvent.total || 0;
          const current = progressEvent.loaded || 0;
          const percentage = Math.floor((current / total) * 100);
          setDownloadPercentage(percentage);
          // console.log(`Downloading file: ${percentage}%`);
          setloadingText(`Downloading file`);
        },
      });
      // downloadFile(downloadResponse, "calculated_margin.xlsx");
      downloadFile(downloadResponse, 'calculated_margin1.csv');
      showAlert('success', 'Excel downloaded successfully');
    } catch (error) {
      console.log(error);
      // showAlert("error", error.message || "An error occurred");
    } finally {
      stopLoading();
      setDownloadPercentage(null);
      setloadingText('');
    }
  };

  return (
    <MarginCalculatorContext.Provider
      value={{
        isLoading,
        startLoading,
        stopLoading,
        loadingText,
        setloadingText,
        downloadPercentage,
        setDownloadPercentage,
        uploadMarginCalculationFile,
        getMainProducts,
        getChildProducts,
        downloadCSV,
        downloadExcel,
        calculateMarginApi,
        marginData,
        setMarginData,
        calculateMarginByProductFile,
      }}
    >
      {children}
    </MarginCalculatorContext.Provider>
  );
};

export default MarginCalculatorContextProvider;

/**
 * Custom hook to use the MarginCalculatorContext.
 * @returns {Object} The context value.
 */
export const useMarginCalculator = () => useContext(MarginCalculatorContext);
